/**
 * The SecureX brand marks, from `docs/brand/`.
 *
 * The mark is a "redacted X": an X whose crossing is sealed behind a plate,
 * with a redaction bar where the crossing would be. The plate is opaque and
 * must match the surface it sits on, which is why `plate` is a prop rather
 * than a constant — a mark on a card needs a different fill from one on the
 * page ground, and getting it wrong shows the arms crossing underneath.
 *
 * Brand rules honoured here, all from `docs/brand/05-palette-scale-clearspace.jpg`:
 *   - one accent only (Brass), never a second
 *   - the X of the wordmark always carries that accent, nothing else does
 *   - below 24px the plate drops out and the X inverts — see `public/favicon.svg`
 */

export const BRAND = {
  obsidian: "#0C0E10",
  shale: "#121519",
  brass: "#D8B26A",
  deepBrass: "#8A6620",
  bone: "#F3F1EC",
  tagline: "The ledger proves everything. It shows nothing.",
  strapline: "ENCRYPTED STATE · SHIELDED TRANSACTIONS",
} as const;

/**
 * The mark alone, sized by `size`, on a ground of `plate`.
 *
 * The strokes take `--accent` rather than `BRAND.brass` so the light scheme
 * gets deep brass automatically: #D8B26A on ivory is too pale to hold an
 * edge, which is the whole reason the brand book specifies a second value.
 */
export function Mark({ size = 28, plate = "var(--chip)" }: { size?: number; plate?: string }) {
  const ink = `var(--accent, ${BRAND.brass})`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <path d="M5 5 L59 59 M59 5 L5 59" fill="none" stroke={ink} strokeWidth="10.5" />
      <rect x="15.25" y="15.25" width="33.5" height="33.5" rx="8"
        fill={plate} stroke={ink} strokeWidth="10.5" />
      <rect x="23" y="29.25" width="18" height="5.5" rx="2.75" fill={ink} />
    </svg>
  );
}

/** The mark inside its chip — the app-icon form, for anywhere it needs a frame. */
export function MarkChip({ size = 34 }: { size?: number }) {
  return (
    <span className="chip" style={{ width: size, height: size, borderRadius: size * 0.27 }}>
      <Mark size={size * 0.625} />
    </span>
  );
}

/**
 * The horizontal lockup: chip, then the wordmark with the X in Brass.
 *
 * The wordmark is live text, not an image, so it stays selectable and
 * scales with the page. It is one `<span>` split in two so the X can take
 * the accent without the reading order breaking — a screen reader still
 * hears "SecureX".
 */
export function Lockup({ note, size = 20 }: { note?: string; size?: number }) {
  return (
    <span className="lockup">
      <MarkChip size={size * 1.7} />
      <span className="lock-text">
        <span className="wordmark" style={{ fontSize: size }}>
          SECURE<span className="x">X</span>
        </span>
        {note && <span className="lock-note">{note}</span>}
      </span>
    </span>
  );
}
